<template>
    <div class="mx-auto w-full max-w-9xl p-3 md:p-4 lg:p-8 h-full">
        <!-- Header Section with better styling -->
        <div class="mb-6">
            <div class="flex items-center gap-3 mb-3">
                <div class="p-2.5 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-lg shadow-md">
                    <AppIcon name="chart-line" class="h-5 w-5 text-white" />
                </div>
                <div>
                    <h1 class="text-3xl font-bold text-slate-900">
                        Industries
                    </h1>
                    <p class="text-slate-600 mt-1">Discover top creators across {{ industriesWithCategories.length }} industries</p>
                </div>
            </div>
        </div>

        <!-- Loading State with better skeletons -->
        <div v-if="isLoading" class="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
            <div v-for="n in 8" :key="n" class="bg-white border border-slate-200 rounded-lg p-6 shadow-sm animate-pulse">
                <div class="flex items-center justify-between mb-4">
                    <div class="flex items-center gap-3">
                        <div class="h-8 w-8 bg-slate-200 rounded"></div>
                        <div class="h-6 bg-slate-200 rounded w-32"></div>
                    </div>
                    <div class="h-5 w-16 bg-slate-100 rounded-full"></div>
                </div>
                <div class="space-y-2">
                    <div class="h-7 bg-slate-100 rounded-full w-24"></div>
                    <div class="h-7 bg-slate-100 rounded-full w-32"></div>
                    <div class="h-7 bg-slate-100 rounded-full w-20"></div>
                </div>
            </div>
        </div>

        <!-- Industries Grid with improved cards -->
        <div v-else-if="industriesWithCategories.length > 0" class="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
            <IndustryCard
                v-for="industry in industriesWithCategories"
                :key="industry.id"
                :industry="industry"
                @industry-click="handleIndustryClick"
                @category-click="handleCategoryClick"
            />
        </div>

        <!-- Empty State with better design -->
        <div v-else class="flex flex-col items-center justify-center py-16">
            <div class="p-5 bg-slate-100 rounded-lg mb-4">
                <AppIcon name="building" class="h-12 w-12 text-slate-400" />
            </div>
            <h3 class="text-lg font-semibold text-slate-900 mb-2">No Industries Found</h3>
            <p class="text-slate-500 text-center max-w-md">
                We couldn't find any industries at the moment. Please try again later.
            </p>
        </div>
    </div>
</template>

<script setup>
import { ref, onMounted, computed, getCurrentInstance } from 'vue'
import { useRouter } from 'vue-router'
import { useStore } from 'vuex'
import _ from 'lodash'
import IndustryCard from '@/components/IndustryCard.vue'
import { axios } from '@/utils/axios'

const router = useRouter()
const store = useStore()
const { proxy } = getCurrentInstance()

const isLoading = ref(false)
const industries = ref([])
const industriesMap = ref({})
const categoriesMap = ref(new Map())

// Default platform - Use the organization's settings
const defaultPlatformId = computed(() => store.state.self.defaultPlatformId)
const platform = ref(defaultPlatformId.value)
const platformContentTypeMap = {
    'youtube': 'video',
    'twitch' : 'stream',
    'instagram' : 'post',
    'tiktok' : 'video',
    'twitter' : 'post',
}
const contentType = platformContentTypeMap[platform.value]

const industriesWithCategories = computed(() => {
    return industries.value.map(industry => {
        const categories = categoriesMap.value.get(industry.code) || []

        return {
            ...industry,
            categories: categories,
            visibleCategories: categories.slice(0, 3),
            hiddenCategoriesCount: Math.max(0, categories.length - 3)
        }
    })
})

const fetchIndustries = async () => {
    try {
        const filters = [
            {
                id: 'platform_id',
                value: platform.value,
            },
            {
                id: 'content_type',
                value: 'video',
            },
            {
                id: 'day_range',
                value: 90,
            }
        ]

        const response = await axios.post(`/discovery/${platform.value}/codes/classifications_topic`, {
            filters,
            pagination: {
                limit: 100,
                skip: 0,
                sort: [{
                    field: `metrics.platforms.${platform.value}.${contentType}.day_365`,
                    direction: 'desc',
                }],
            }
        })

        if (response.data.payload.codes) {
            industries.value = response.data.payload.codes.filter(industry => industry.code !== null)

            // Create a map for quick lookup
            industriesMap.value = _.keyBy(industries.value, 'code')
        }
    } catch (error) {
        console.error('Error fetching industries:', error)
        console.log(error.message)
    }
}

const fetchCategoriesForIndustries = async () => {
    console.log('Fetching all categories in single API call...')

    try {
        const filters = [
            {
                id: 'platform_id',
                value: platform.value,
            },
            {
                id: 'content_type',
                value: contentType,
            },
            {
                id: 'day_range',
                value: 90,
            }
            // No parent filter - get ALL categories
        ]

        const response = await axios.post(`/discovery/${platform.value}/codes/classifications_category`, {
            filters,
            pagination: {
                limit: 1000, // Increase limit to get all categories
                skip: 0,
                sort: [{
                    field: `metrics.platforms.${platform.value}.${platformContentTypeMap[platform.value]}.day_90`,
                    direction: 'desc',
                }],
            }
        })

        const allCategories = response.data.payload.codes
            ? response.data.payload.codes.filter(category => category.code !== null)
            : []

        console.log(`Fetched ${allCategories.length} total categories`)

        // Group categories by their parent industry
        const categoriesMap = new Map()

        // Initialize map with empty arrays for all industries
        industries.value.forEach(industry => {
            categoriesMap.set(industry.code, [])
        })

        // Group categories by parent
        allCategories.forEach(category => {
            if (category.parent) {
                // Handle both string and array formats for parent
                const parents = Array.isArray(category.parent) ? category.parent : [category.parent]

                parents.forEach(parentCode => {
                    if (categoriesMap.has(parentCode)) {
                        categoriesMap.get(parentCode).push(category)
                    }
                })
            }
        })

        // Sort categories within each industry - "other" always at the end
        categoriesMap.forEach((categories, industryCode) => {
            categories.sort((a, b) => {
                // Push "other" to the end
                if (a.code === 'other' || a.label?.toLowerCase() === 'other') return 1
                if (b.code === 'other' || b.label?.toLowerCase() === 'other') return -1

                // Otherwise maintain current order
                return 0
            })
        })

        console.log('Categories grouped by industry:', categoriesMap)
        return categoriesMap

    } catch (error) {
        console.error('Error fetching all categories:', error)

        // Return empty map for all industries on error
        const categoriesMap = new Map()
        industries.value.forEach(industry => {
            categoriesMap.set(industry.code, [])
        })
        return categoriesMap
    }
}

const handleIndustryClick = (industry) => {
    router.push({
        name: 'industry-detail',
        params: { industry_name: industry.code }
    })
}

const handleCategoryClick = (industry, category) => {
    router.push({
        name: 'industry-category-detail',
        params: {
            industry_name: industry.code,
            category_name: category.code
        }
    })
}

function getFilter(filters, key) {
    return filters.find(f => f.id === key)?.value
}

onMounted(async () => {
    isLoading.value = true

    try {
        // First fetch industries
        await fetchIndustries()

        // Then fetch categories for each industry
        if (industries.value.length > 0) {
            categoriesMap.value = await fetchCategoriesForIndustries()
        }
    } finally {
        isLoading.value = false
    }
})
</script>